import React, { useRef } from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { DocumentIcon } from "../../../../assets/ui/icons";
import { FileDropzone, DOC_ACCEPT } from "./FileDropzone";
import { documentDisplayName } from "./documentSpecs";

/* One of the three document slots. */
export const DocumentSlot = ({
  spec,
  url,
  uploading = false,
  error,
  onUpload,
  className = "",
}) => {
  const inputRef = useRef(null);

  return (
    <div className={className}>
      <p className="m-0 mb-2 text-[13px] font-bold text-[var(--ink)] font-sans">
        {spec.label}
      </p>

      {uploading ? (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            "flex h-[152px] items-center justify-center rounded-[13px]",
            "border-[1.5px] border-dashed border-[var(--dropzone-border)]",
            "bg-[var(--field-inset)] text-[13px] text-[var(--muted)] font-sans"
          )}
        >
          Uploading…
        </div>
      ) : url ? (
        <div className="overflow-hidden rounded-[13px] border border-[var(--line)] bg-white">
          {/* The preview panel. A stored document is behind an authenticated URL, so an <img> cannot fetch it — the live screen fetches each one with the bearer token... */}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex h-[104px] items-center justify-center gap-2",
              "bg-[var(--field-inset)] text-[13px] text-[var(--muted)] font-sans",
              "transition-colors hover:text-[var(--coral)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
              "focus-visible:ring-[var(--coral)]"
            )}
          >
            <span aria-hidden="true">
              <DocumentIcon size={15} />
            </span>
            Document preview
          </a>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] px-4 py-3">
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[var(--ink)] font-sans">
              {/* The document's own name when the URL is a masked token
                  rather than a filename — see documentDisplayName. */}
              {documentDisplayName(url, spec.section)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => inputRef.current?.click()}
            >
              Update
            </Button>
          </div>

          {/* The Update button drives this rather than a second dropzone, so a
              filled slot keeps the design's panel. */}
          <input
            ref={inputRef}
            type="file"
            accept={DOC_ACCEPT}
            aria-label={spec.updateLabel}
            onChange={(e) => onUpload?.(e.target.files?.[0])}
            className="sr-only"
          />
        </div>
      ) : (
        <FileDropzone
          label={spec.label}
          files={[]}
          onChange={(next) => onUpload?.(next?.[0])}
          error={error}
        />
      )}

      {/* Only when there is no dropzone to carry it: FileDropzone renders the
          message itself, and two alerts for one failure is one too many. */}
      {error && (uploading || url) && (
        <p
          role="alert"
          className="m-0 mt-2 text-[12.5px] font-semibold text-[var(--coral-deep)] font-sans"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default DocumentSlot;
